import React from 'react';
import { useAuth } from '../../contexts/AuthContext';

/*
 * The company picker, in the HR header.
 *
 * Rendered ONLY when the session holds more than one active membership, so the single-company
 * case — every recruiter today — is visually unchanged. For the two-company case it is not a
 * convenience: requireCompany() answers 400 company_required on any tenant-scoped route when
 * more than one membership exists and nothing says which, so without a way to choose, the HR app
 * does not work at all for that recruiter.
 *
 * Design system: the .input control the admin company forms already use, at the compact size,
 * with a label-editorial caption. No gradients, no glass blur, no oversized radii.
 */
const CompanySwitcher: React.FC = () => {
  const { companies, activeCompanyId, setActiveCompany } = useAuth();

  if (companies.length < 2) return null;

  const handleChange = (id: string) => {
    if (id === activeCompanyId) return;
    setActiveCompany(id);

    /*
     * A full navigation, not a router push. Nothing in this app caches server data centrally:
     * every page fetches in its own useEffect on mount, and App.tsx's PageTransition is keyed on
     * pathname, so a router navigation to the route you are already on re-renders without
     * refetching — the previous tenant's jobs would sit on screen under the new company's name.
     * Worse, any id in the current URL (/hr/jobs/:id, /hr/applications/:id) belongs to the tenant
     * being left and would 404 under the new one. Landing on /hr/jobs from scratch is the only
     * state that is correct for every starting page.
     *
     * setActiveCompany has already written the choice to localStorage synchronously, so the
     * reload's /api/me re-seeds the same company rather than snapping back to entry 0.
     */
    window.location.assign('/hr/jobs');
  };

  return (
    <div className="flex items-center gap-2 pr-4 border-r border-hairline">
      <label htmlFor="active-company" className="label-editorial hidden lg:block">
        Acting as
      </label>
      <select
        id="active-company"
        aria-label="Acting as company"
        className="input"
        style={{ padding: '0.375rem 0.5rem', fontSize: '0.8125rem', maxWidth: 180 }}
        // Non-null in practice: pickActiveCompanyId returns an id whenever the membership list is
        // non-empty, and this component only renders with two or more entries.
        value={activeCompanyId ?? ''}
        onChange={(e) => handleChange(e.target.value)}
      >
        {companies.map((c) => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
    </div>
  );
};

export default CompanySwitcher;
